import React, { useState } from 'react';
import { Box, Typography, Button, Container, Chip, Stack } from '@mui/material';
import { useTheme } from '../../../ThemeContext';
import { GooglePlayIcon, AppleIcon } from '../../common/StoreIcons';
import { PLAY_STORE_URL, APP_STORE_URL } from '../../../constants/links';
import { AppQRScannerModal } from '../../common/AppQRScanner';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import VideoSettingsRoundedIcon from '@mui/icons-material/VideoSettingsRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import QrCodeScannerRoundedIcon from '@mui/icons-material/QrCodeScannerRounded';
 
const ProductsHeroSection = ({ onScrollToProduct }) => {
  const { isDarkMode } = useTheme();
  const [qrModalOpen, setQrModalOpen] = useState(false);

  return (
    <Box
      sx={{
        position: 'relative',
        pt: { xs: 10, md: 14 },
        pb: { xs: 10, md: 12 },
        color: isDarkMode ? '#fff' : '#0d1530',
        overflow: 'hidden',
        background: isDarkMode
          ? 'radial-gradient(circle at 50% 20%, #0d1e3d 0%, #050a14 70%)'
          : 'radial-gradient(circle at 50% 20%, #e0f2fe 0%, #f8fafc 70%)',
      }}
    >
      {/* Background cinematic overlay */}
      <Box
        component="img"
        src="https://cdn.britannica.com/18/176618-050-6268BB99/David-Lean-filming-Doctor-Zhivago.jpg"
        alt="Cinema Production"
        sx={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          objectFit: 'cover',
          zIndex: 0,
          opacity: isDarkMode ? 0.22 : 0.08,
          filter: 'grayscale(40%)',
        }}
      />

      {/* Decorative Glowing Orbs */}
      <Box
        sx={{
          position: 'absolute',
          top: '15%',
          left: '10%',
          width: '320px',
          height: '320px',
          background: 'radial-gradient(circle, rgba(7, 164, 245, 0.35) 0%, transparent 70%)',
          filter: 'blur(70px)',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '30%',
          right: '8%',
          width: '350px',
          height: '350px',
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.25) 0%, transparent 70%)',
          filter: 'blur(80px)',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '5%',
          left: '40%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(6, 182, 212, 0.25) 0%, transparent 70%)',
          filter: 'blur(70px)',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
        {/* Pill Tag */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 2.2,
            py: 0.8,
            borderRadius: '30px',
            background: isDarkMode ? 'rgba(7, 164, 245, 0.12)' : 'rgba(7, 164, 245, 0.1)',
            border: isDarkMode ? '1px solid rgba(7, 164, 245, 0.3)' : '1px solid rgba(7, 164, 245, 0.25)',
            mb: 3,
            boxShadow: '0 4px 16px rgba(7, 164, 245, 0.15)',
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: '#10b981',
              boxShadow: '0 0 10px #10b981',
            }}
          />
          <Typography
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.82rem' },
              fontWeight: 800,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: isDarkMode ? '#38bdf8' : '#0284c7',
            }}
          >
            The Filmhook Ecosystem • 3 Dedicated Products
          </Typography>
        </Box>

        {/* Main Headline */}
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '2.3rem', sm: '3.2rem', md: '4rem' },
            fontWeight: 900,
            lineHeight: { xs: 1.2, md: 1.15 },
            letterSpacing: '-0.02em',
            mb: 2.5,
            color: isDarkMode ? '#ffffff' : '#0d1530',
          }}
        >
          One Connected Ecosystem.{' '}
          <Box
            component="span"
            sx={{
              background: 'linear-gradient(135deg, #07a4f5 0%, #a855f7 50%, #ec4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: { xs: 'inline', sm: 'inline-block' },
            }}
          >
            Three Cinema Powerhouses.
          </Box>
        </Typography>

        {/* Subtitle */}
        <Typography
          variant="body1"
          sx={{
            fontSize: { xs: '0.98rem', sm: '1.15rem', md: '1.25rem' },
            color: isDarkMode ? '#94a3b8' : '#475569',
            maxWidth: 820,
            mx: 'auto',
            mb: 5,
            lineHeight: 1.7,
            fontWeight: 400,
          }}
        >
          Empowering the entire cinema lifecycle: Discover and cast talent on{' '}
          <Box
            component="span"
            onClick={() => onScrollToProduct && onScrollToProduct('apps')}
            sx={{
              color: isDarkMode ? '#38bdf8' : '#0284c7',
              fontWeight: 800,
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Filmhook Apps
          </Box>
          , edit and grade in{' '}
          <Box
            component="span"
            onClick={() => onScrollToProduct && onScrollToProduct('editor')}
            sx={{
              color: '#a855f7',
              fontWeight: 800,
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Filmhook Editor
          </Box>
          , and unlock generative filmmaking intelligence with{' '}
          <Box
            component="span"
            onClick={() => onScrollToProduct && onScrollToProduct('ai')}
            sx={{
              color: '#06b6d4',
              fontWeight: 800,
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Filmhook AI
          </Box>
          .
        </Typography>

        {/* Quick Product Jumper / Interactive Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 2.5,
            maxWidth: 960,
            mx: 'auto',
            mb: 6,
          }}
        >
          {/* Card 1: Filmhook Apps */}
          <Box
            onClick={() => onScrollToProduct && onScrollToProduct('apps')}
            sx={{
              p: 2.5,
              borderRadius: '20px',
              background: isDarkMode
                ? 'rgba(15, 23, 42, 0.7)'
                : '#ffffff',
              border: isDarkMode
                ? '1px solid rgba(7, 164, 245, 0.3)'
                : '1px solid rgba(7, 164, 245, 0.2)',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: isDarkMode
                ? '0 10px 30px rgba(0,0,0,0.4)'
                : '0 10px 30px rgba(7, 164, 245, 0.08)',
              '&:hover': {
                transform: 'translateY(-4px)',
                borderColor: '#07a4f5',
                boxShadow: '0 16px 36px rgba(7, 164, 245, 0.25)',
              },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '14px',
                background: 'rgba(7, 164, 245, 0.15)',
                color: '#07a4f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1.5,
              }}
            >
              <PhoneIphoneRoundedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Chip
              label="Live on iOS & Android"
              size="small"
              sx={{
                height: 22,
                fontSize: '0.68rem',
                fontWeight: 800,
                bgcolor: 'rgba(16, 185, 129, 0.15)',
                color: '#10b981',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                mb: 1,
              }}
            />
            <Typography variant="h6" fontWeight={800} sx={{ color: isDarkMode ? '#fff' : '#0f172a', fontSize: '1.1rem' }}>
              Filmhook Apps
            </Typography>
            <Typography variant="caption" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', mt: 0.5 }}>
              Social, Casting, Auditions & Talent Directory
            </Typography>
          </Box>

          {/* Card 2: Filmhook Editor */}
          <Box
            onClick={() => onScrollToProduct && onScrollToProduct('editor')}
            sx={{
              p: 2.5,
              borderRadius: '20px',
              background: isDarkMode
                ? 'rgba(15, 23, 42, 0.7)'
                : '#ffffff',
              border: isDarkMode
                ? '1px solid rgba(139, 92, 246, 0.3)'
                : '1px solid rgba(139, 92, 246, 0.2)',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: isDarkMode
                ? '0 10px 30px rgba(0,0,0,0.4)'
                : '0 10px 30px rgba(139, 92, 246, 0.08)',
              '&:hover': {
                transform: 'translateY(-4px)',
                borderColor: '#8b5cf6',
                boxShadow: '0 16px 36px rgba(139, 92, 246, 0.25)',
              },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '14px',
                background: 'rgba(139, 92, 246, 0.15)',
                color: '#8b5cf6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1.5,
              }}
            >
              <VideoSettingsRoundedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Chip
              label="Now on Google Play"
              size="small"
              sx={{
                height: 22,
                fontSize: '0.68rem',
                fontWeight: 800,
                bgcolor: 'rgba(139, 92, 246, 0.2)',
                color: '#c084fc',
                border: '1px solid rgba(139, 92, 246, 0.4)',
                mb: 1,
              }}
            />
            <Typography variant="h6" fontWeight={800} sx={{ color: isDarkMode ? '#fff' : '#0f172a', fontSize: '1.1rem' }}>
              Filmhook Editor
            </Typography>
            <Typography variant="caption" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b', mt: 0.5 }}>
              Pro Timeline Video Studio & Color Grading
            </Typography>
          </Box>

          {/* Card 3: Filmhook AI */}
          <Box
            onClick={() => onScrollToProduct && onScrollToProduct('ai')}
            sx={{
              p: 2.5,
              borderRadius: '20px',
              background: isDarkMode
                ? 'rgba(15, 23, 42, 0.7)'
                : '#ffffff',
              border: isDarkMode
                ? '1px solid rgba(6, 182, 212, 0.3)'
                : '1px solid rgba(6, 182, 212, 0.2)',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: isDarkMode
                ? '0 10px 30px rgba(0,0,0,0.4)'
                : '0 10px 30px rgba(6, 182, 212, 0.08)',
              '&:hover': {
                transform: 'translateY(-4px)',
                borderColor: '#06b6d4',
                boxShadow: '0 16px 36px rgba(6, 182, 212, 0.25)',
              },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '14px',
                background: 'rgba(6, 182, 212, 0.15)',
                color: '#06b6d4',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1.5,
              }}
            >
              <AutoAwesomeRoundedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Chip
              label="Live on Play Store"
              size="small"
              sx={{
                height: 22,
                fontSize: '0.68rem',
                fontWeight: 800,
                bgcolor: 'rgba(6, 182, 212, 0.15)',
                color: '#06b6d4',
                border: '1px solid rgba(6, 182, 212, 0.35)',
                mb: 1,
              }}
            />
            <Typography variant="h6" fontWeight={800} sx={{ color: isDarkMode ? '#fff' : '#0f172a', fontSize: '1.1rem' }}>
              Filmhook AI
            </Typography>
            <Typography variant="caption" sx={{ color: isDarkMode ? '#94A3B8' : '#64748B', mt: 0.5 }}>
              Generative Scripts, Storyboards & Smart Casting
            </Typography>
          </Box>
        </Box>

        {/* Direct Action Buttons */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="center">
          <Button
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<GooglePlayIcon size={20} />}
            sx={{
              background: 'linear-gradient(90deg, #07a4f5 0%, #5dade2 100%)',
              color: 'white',
              fontWeight: 800,
              fontSize: '0.92rem',
              borderRadius: '30px',
              px: 3.5,
              py: 1.3,
              textTransform: 'none',
              boxShadow: '0 8px 24px rgba(7, 164, 245, 0.4)',
              '&:hover': {
                background: 'linear-gradient(90deg, #068bc2 0%, #3498db 100%)',
                boxShadow: '0 12px 30px rgba(7, 164, 245, 0.55)',
              },
            }}
          >
            Google Play Store
          </Button>

          <Button
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<AppleIcon size={20} />}
            sx={{
              background: isDarkMode ? 'rgba(255,255,255,0.08)' : '#ffffff',
              border: isDarkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(7, 164, 245, 0.3)',
              color: isDarkMode ? '#ffffff' : '#0d1530',
              fontWeight: 800,
              fontSize: '0.92rem',
              borderRadius: '30px',
              px: 3.5,
              py: 1.3,
              textTransform: 'none',
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
              '&:hover': {
                background: isDarkMode ? 'rgba(255,255,255,0.15)' : '#f8fafc',
                boxShadow: '0 12px 30px rgba(7, 164, 245, 0.2)',
              },
            }}
          >
            Apple App Store (iOS)
          </Button>

          <Button
            onClick={() => setQrModalOpen(true)}
            startIcon={<QrCodeScannerRoundedIcon />}
            sx={{
              background: isDarkMode ? 'rgba(7, 164, 245, 0.12)' : 'rgba(7, 164, 245, 0.08)',
              border: '1.5px solid rgba(7, 164, 245, 0.35)',
              color: '#07a4f5',
              fontWeight: 800,
              fontSize: '0.92rem',
              borderRadius: '30px',
              px: 3.2,
              py: 1.3,
              textTransform: 'none',
              boxShadow: '0 4px 16px rgba(7, 164, 245, 0.2)',
              '&:hover': {
                background: '#07a4f5',
                color: '#ffffff',
              },
            }}
          >
            Scan QR to Install
          </Button>

          <Button
            onClick={() => onScrollToProduct && onScrollToProduct('apps')}
            endIcon={<ArrowDownwardRoundedIcon />}
            sx={{
              color: isDarkMode ? '#94a3b8' : '#475569',
              fontWeight: 700,
              fontSize: '0.92rem',
              borderRadius: '30px',
              px: 3,
              py: 1.3,
              textTransform: 'none',
              '&:hover': {
                color: '#07a4f5',
                background: isDarkMode ? 'rgba(7, 164, 245, 0.08)' : 'rgba(7, 164, 245, 0.05)',
              },
            }}
          >
            Explore Showcase
          </Button>
        </Stack>
      </Container>

      {/* Global QR Scanner Modal */}
      <AppQRScannerModal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        defaultAppId="filmhook-android"
      />
    </Box>
  );
};

export default ProductsHeroSection;
